/** NOTE: This component is shared with MKP!
 * Prevent regressions by spoofing as a tenant when testing. */
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import storeLoader from 'app/stores/storeLoader';
import { isEqual, noop } from 'lodash';
import formatMetricValue from 'app/util/formatMetricValue';

import { AiFillBug, AiOutlineComment } from 'react-icons/ai';

import { formatDateTime, DEFAULT_DATETIME_FORMAT_WITH_SECONDS } from 'core/util/dateUtils';
import { Box, Flex, Link, MenuDivider, Tag, Text } from 'core/components';
import DashboardLink from 'app/components/links/DashboardLink';
import AdminTable from 'app/components/admin/AdminTable';
import ClearAlarmDialog from 'app/views/alerting/components/ClearAlarmDialog';
import TargetRenderer from 'app/views/protect/ddos/analyze/components/TargetRenderer';
import { UnackAlertButton } from 'app/views/alerting/components/UnackAlertButton';
import CommentDialog from 'app/components/comments/CommentDialog';
import {
  DeviceStatusTag,
  InterfaceOperAdminStatusTag,
  SessionStateRenderer
} from 'app/views/metrics/tables/StatusIndicators';

import { metricLabelRenderer } from 'app/components/metric/metricRenderers';
import MitigationLink from 'app/components/links/MitigationLink';
import AlertDebugDialog from 'app/views/alerting/components/AlertDebugDialog';
import AlertSeverityBars from 'app/views/alerting/components/AlertSeverityBars';
import { ALERT_TABLE_COLUMNS } from '@kentik/ui-shared/alerting/constants';
import AckDialog from './AckDialog';

import SilenceAlertButton from './SilenceAlertButton';
import SuppressAlertButton from './SuppressAlertButton';
import NmsNativeAlertDimensionRenderer from './nms/NmsNativeAlertDimensionRenderer';
import NmsNativeAlertMetricListRenderer from './nms/NmsNativeAlertMetricListRenderer';

const ROW_HEIGHT = 60;
const CONTEXT_NAME_ALERT_TABLE = 'Alert Table';
const CONTEXT_TYPE_ALERT_TABLE = 'alert_table';

@storeLoader('$alerting.policyCollection')
@inject('$app', '$auth', '$mkp', '$exports', '$comments', '$suppressions', '$users', '$journeys')
@observer
class AlertingTable extends Component {
  static propTypes = {
    onRowClick: PropTypes.func,
    showTenantAlerts: PropTypes.bool,
    tableProps: PropTypes.object,
    useVirtualizedTable: PropTypes.bool
  };

  static defaultProps = {
    onRowClick: noop,
    showTenantAlerts: false,
    tableProps: {},
    useVirtualizedTable: true
  };

  state = {
    commentModel: null,
    debugModel: null,
    clearAlertModel: null,
    loadingSuppressions: true,
    loadingSilences: true
  };

  componentDidMount() {
    const { $app, $suppressions, $alerting, $exports, $users } = this.props;

    $exports.getSettings().then(({ sortState }) => {
      if (sortState && !isEqual($alerting.collection.sortState, sortState)) {
        $alerting.collection.serverSort(sortState.field, sortState.direction);
      }
    });

    if ($app.isSubtenant) {
      this.setState({ loadingSuppressions: false, loadingSilences: false });
    } else {
      $suppressions.collection.fetch().then(() => {
        this.setState({ loadingSuppressions: false });
      });
      $alerting.silenceCollection.fetch().then(() => {
        this.setState({ loadingSilences: false });
      });
      $users.collection.fetch();
    }

    // Initialize AI context
    this.updateTableContext();
  }

  componentDidUpdate(prevProps) {
    const { collection, $alerting } = this.props;
    const activeCollection = collection || $alerting.collection;
    const prevCollection = prevProps.collection || prevProps.$alerting.collection;

    // Update context if collection size or content changed
    if (activeCollection.size !== prevCollection.size || activeCollection !== prevCollection) {
      this.updateTableContext();
    }
  }

  componentWillUnmount() {
    const { $journeys } = this.props;

    // Clean up table context
    const tableContext = Array.from($journeys.activeChatContext).find((ctx) => ctx.type === CONTEXT_TYPE_ALERT_TABLE);
    if (tableContext) {
      $journeys.removeChatContext(tableContext);
    }
  }

  /**
   * Updates the AI assistant context with current alert table state.
   * Provides comprehensive information about visible alerts, their severity, and status.
   */
  updateTableContext = () => {
    const { $journeys, collection, $alerting, $app } = this.props;

    const activeCollection = collection || $alerting.collection;

    const totalAlerts = activeCollection.size;
    const alerts = activeCollection.models || [];

    if (totalAlerts === 0) {
      // Update minimal context when no alerts
      $journeys.updateChatContext({
        type: CONTEXT_TYPE_ALERT_TABLE,
        name: CONTEXT_NAME_ALERT_TABLE,
        context: 'Alert Table\nState: OPEN\n\nNo alerts currently visible.',
        placeholder: 'Ask about alerts',
        hidden: true
      });
      return;
    }

    // Analyze alert collection
    const severityBreakdown = {};
    const stateBreakdown = {};
    const typeBreakdown = {};
    const policyBreakdown = {};
    let ackedCount = 0;
    let silencedCount = 0;
    let activeCount = 0;
    let clearedCount = 0;

    alerts.forEach((alert) => {
      const severity = alert.get('severity') || 'Unknown';
      const state = alert.get('stateLabel') || 'Unknown';
      const type = alert.topLevelApplication || 'Unknown';
      const policyName = alert.get('policy.name') || 'Unknown';

      severityBreakdown[severity] = (severityBreakdown[severity] || 0) + 1;
      stateBreakdown[state] = (stateBreakdown[state] || 0) + 1;
      typeBreakdown[type] = (typeBreakdown[type] || 0) + 1;

      // Track top policies (limit to prevent excessive context)
      if (Object.keys(policyBreakdown).length < 10) {
        policyBreakdown[policyName] = (policyBreakdown[policyName] || 0) + 1;
      }

      if (alert.isAcked) {
        ackedCount += 1;
      }
      if (alert.get('silenceModel')) {
        silencedCount += 1;
      }
      if (alert.isActive) {
        activeCount += 1;
      }
      if (alert.isCleared) {
        clearedCount += 1;
      }
    });

    // Build context parts
    const contextParts = [
      'ALERT TABLE',
      'State: OPEN',
      `\nTOTAL ALERTS: ${totalAlerts}`,
      `Active: ${activeCount}`,
      `Cleared: ${clearedCount}`,
      !$app.isSubtenant && ackedCount > 0 ? `Acknowledged: ${ackedCount}` : null,
      !$app.isSubtenant && silencedCount > 0 ? `Silenced: ${silencedCount}` : null
    ].filter(Boolean);

    // Add severity breakdown
    const sortedSeverities = Object.entries(severityBreakdown).sort(([, a], [, b]) => b - a);
    if (sortedSeverities.length > 0) {
      contextParts.push('\nSEVERITY BREAKDOWN:');
      sortedSeverities.forEach(([severity, count]) => {
        contextParts.push(`  ${severity}: ${count} alert${count !== 1 ? 's' : ''}`);
      });
    }

    // Add type breakdown
    const sortedTypes = Object.entries(typeBreakdown).sort(([, a], [, b]) => b - a);
    if (sortedTypes.length > 0) {
      contextParts.push('\nALERT TYPES:');
      sortedTypes.forEach(([type, count]) => {
        contextParts.push(`  ${type}: ${count} alert${count !== 1 ? 's' : ''}`);
      });
    }

    // Add top policies
    const sortedPolicies = Object.entries(policyBreakdown).sort(([, a], [, b]) => b - a);
    if (sortedPolicies.length > 0) {
      contextParts.push('\nTOP POLICIES:');
      sortedPolicies.slice(0, 5).forEach(([policy, count]) => {
        contextParts.push(`  ${policy}: ${count} alert${count !== 1 ? 's' : ''}`);
      });
      if (sortedPolicies.length > 5) {
        contextParts.push(`  ...and ${sortedPolicies.length - 5} more policies`);
      }
    }

    // Add sample of recent alerts (top 3)
    if (alerts.length > 0) {
      contextParts.push('\nRECENT ALERTS (sample):');
      alerts.slice(0, 3).forEach((alert, index) => {
        const summary = this.getAlertSummary(alert);
        contextParts.push(`${index + 1}. ${summary}`);
      });
      if (alerts.length > 3) {
        contextParts.push(`...and ${alerts.length - 3} more alerts`);
      }
    }

    // Update the context
    $journeys.updateChatContext({
      type: CONTEXT_TYPE_ALERT_TABLE,
      name: CONTEXT_NAME_ALERT_TABLE,
      context: contextParts.join('\n'),
      placeholder: 'Ask about alerts',
      hidden: true
    });
  };

  /**
   * Builds a meaningful summary for an alert for AI context.
   * Used by AdminTable's context tracking.
   */
  getAlertSummary = (model) => {
    const parts = [];

    const policyName = model.get('policy.name');
    const severity = model.get('severity');
    const stateLabel = model.get('stateLabel');
    const application = model.topLevelApplication;
    const startTime = model.get('start_time');
    const duration = model.displayDuration;

    if (policyName) {
      parts.push(`Policy: ${policyName}`);
    }
    if (severity) {
      parts.push(`Severity: ${severity}`);
    }
    if (stateLabel) {
      parts.push(`State: ${stateLabel}`);
    }
    if (application) {
      parts.push(`Type: ${application}`);
    }
    if (startTime) {
      parts.push(`Started: ${formatDateTime(startTime, DEFAULT_DATETIME_FORMAT_WITH_SECONDS)}`);
    }
    if (duration) {
      parts.push(`Duration: ${duration}`);
    }

    // Add ack state if applicable
    if (model.isAcked) {
      parts.push(model.isAutoAcked ? 'Auto-acknowledged' : 'Acknowledged');
    }

    // Add silence state if applicable
    if (model.get('silenceModel')) {
      parts.push('Silenced');
    }

    return parts.join(', ');
  };

  get defaultColumns() {
    const { $app } = this.props;

    const defaultColumns = ['severity', 'stateLabel', 'type', 'policyName', 'primaryDimension', 'metric', 'startTime'];

    if (!$app.isSubtenant) {
      defaultColumns.push('silenceModel', 'ackStateLabel');
    }

    return defaultColumns;
  }

  get columns() {
    const { $auth, $users, $app, $mkp, showTenantAlerts, hideColumns } = this.props;

    let columns = [
      {
        label: 'Severity',
        name: ALERT_TABLE_COLUMNS.SEVERITY,
        id: ALERT_TABLE_COLUMNS.SEVERITY,
        value: ALERT_TABLE_COLUMNS.SEVERITY,
        width: 120,
        ellipsis: false,
        serverSortable: true,
        renderer: ({ value }) => <AlertSeverityBars severity={value} showLabel />
      },

      {
        label: 'Alert State',
        name: ALERT_TABLE_COLUMNS.STATE_LABEL,
        id: ALERT_TABLE_COLUMNS.STATE_LABEL,
        value: ALERT_TABLE_COLUMNS.STATE_LABEL,
        computed: true,
        width: 90,
        ellipsis: false,
        serverSortable: true,
        sortField: 'state',
        renderer: ({ model, value }) => (
          <Tag intent={model.isActive ? 'danger' : 'success'} width="100%" textAlign="center">
            {value}
          </Tag>
        )
      },

      {
        label: 'Type',
        name: ALERT_TABLE_COLUMNS.APPLICATION,
        id: ALERT_TABLE_COLUMNS.APPLICATION,
        value: ALERT_TABLE_COLUMNS.APPLICATION,
        computed: true,
        width: 100,
        ellipsis: false,
        serverSortable: true,
        renderer: ({ value }) => (value ? <Tag>{value}</Tag> : null)
      },

      {
        label: 'Policy',
        name: ALERT_TABLE_COLUMNS.POLICY_NAME,
        id: ALERT_TABLE_COLUMNS.POLICY_NAME,
        value: ALERT_TABLE_COLUMNS.POLICY_NAME,
        computed: true,
        flexBasis: 150,
        minWidth: 150,
        serverSortable: true,
        uiSortLogic: true
      },

      {
        label: 'Policy ID',
        name: ALERT_TABLE_COLUMNS.POLICY_ID,
        id: ALERT_TABLE_COLUMNS.POLICY_ID,
        value: ALERT_TABLE_COLUMNS.POLICY_ID,
        flexBasis: 80,
        minWidth: 80,
        serverSortable: true,
        uiSortLogic: true,
        renderer: ({ model }) => model.get('policy.id')
      },

      {
        label: 'Dimensions',
        name: ALERT_TABLE_COLUMNS.PRIMARY_DIMENSION,
        id: ALERT_TABLE_COLUMNS.PRIMARY_DIMENSION,
        value: ALERT_TABLE_COLUMNS.PRIMARY_DIMENSION,
        sortField: 'primary_dimension',
        flexBasis: 200,
        minWidth: 200,
        computed: true,
        ellipsis: false,
        serverSortable: false,
        sortable: false,
        renderer: ({ model }) =>
          model.isNmsPolicy ? (
            <NmsNativeAlertDimensionRenderer maxDimensionsToShow={3} alertModel={model} />
          ) : (
            <TargetRenderer model={model} />
          )
      },

      {
        label: 'Metric',
        name: ALERT_TABLE_COLUMNS.METRIC,
        id: ALERT_TABLE_COLUMNS.METRIC,
        value: ALERT_TABLE_COLUMNS.METRIC,
        sortable: false,
        ellipsis: false,
        flexBasis: 200,
        minWidth: 150,
        flexDirection: 'column',
        alignItems: 'flex-start',
        renderer: ({ model }) => (
          <Flex flexDirection="column" gap="4px" alignItems="flex-start">
            {this.renderMetricColumn(model)}
          </Flex>
        )
      },

      {
        label: 'Mitigation ID',
        name: ALERT_TABLE_COLUMNS.MITIGATION_ID,
        id: ALERT_TABLE_COLUMNS.MITIGATION_ID,
        value: ALERT_TABLE_COLUMNS.MITIGATION_ID,
        computed: true,
        flexBasis: 50,
        minWidth: 118,
        ellipsis: false,
        sortable: false,
        serverSortable: false,
        renderer: ({ value }) =>
          value ? (
            <MitigationLink mb={1} intent="primary" small blank id={value} isSubtenant={$app.isSubtenant}>
              {value}
            </MitigationLink>
          ) : (
            <Text muted>None</Text>
          )
      },

      {
        label: 'Alert ID',
        name: ALERT_TABLE_COLUMNS.ID,
        id: ALERT_TABLE_COLUMNS.ID,
        value: ALERT_TABLE_COLUMNS.ID,
        flexBasis: 150,
        minWidth: 150,
        ellipsis: false,
        serverSortable: true,
        sortField: 'alarm_id',
        renderer: ({ value }) => (
          <Link small to={`/v4/alerting/${value}`} blank>
            {value}
          </Link>
        )
      },
      {
        label: `Time (${$auth.userTimezone})`,
        name: ALERT_TABLE_COLUMNS.START_TIME,
        id: ALERT_TABLE_COLUMNS.START_TIME,
        value: ALERT_TABLE_COLUMNS.START_TIME,
        flexBasis: 60,
        minWidth: 180,
        ellipsis: false,
        flexDirection: 'column',
        alignItems: 'stretch',
        sortField: 'start_time',
        serverSortable: true,
        renderer: ({ model }) => {
          const startTime = model.get('start_time');
          const endTime = model.get('end_time');

          return (
            <>
              <Flex gap="4px">
                <Text muted>Start:</Text>
                <Text>{formatDateTime(startTime, DEFAULT_DATETIME_FORMAT_WITH_SECONDS)}</Text>
              </Flex>

              {model.isCleared && (
                <Flex gap="4px">
                  <Text muted>Clear: </Text>
                  {endTime && <Text>{formatDateTime(endTime, DEFAULT_DATETIME_FORMAT_WITH_SECONDS)}</Text>}
                </Flex>
              )}
              <Flex gap="4px">
                <Text as="div" muted>
                  Duration:
                </Text>
                <Text as="div">{model.displayDuration}</Text>
              </Flex>
            </>
          );
        }
      }
    ];

    if (!$app.isSubtenant) {
      columns.push(
        ...[
          {
            label: 'Tenant',
            name: ALERT_TABLE_COLUMNS.TENANT,
            id: ALERT_TABLE_COLUMNS.TENANT,
            value: ALERT_TABLE_COLUMNS.TENANT,
            computed: true,
            flexBasis: 80,
            minWidth: 150,
            sortable: false,
            hidden: !showTenantAlerts || $mkp.tenants.tenantAlertingOptions.length === 0
          },
          {
            label: 'Silence State',
            name: ALERT_TABLE_COLUMNS.SILENCE_MODEL,
            id: ALERT_TABLE_COLUMNS.SILENCE_MODEL,
            value: ALERT_TABLE_COLUMNS.SILENCE_MODEL,
            computed: true,
            sortable: false,
            width: 160,
            renderer: ({ value }) => {
              const silenceModel = value;

              if (silenceModel) {
                const { startTime, startDetails = {}, expirationDetails = {} } = silenceModel;
                return (
                  <Box>
                    <Text as="div">
                      Silence
                      {startDetails.started ? `d for ${expirationDetails.duration}` : ' pending'}
                    </Text>
                    {startDetails.started && (
                      <Text as="div" muted>
                        Started: {startTime}
                      </Text>
                    )}
                    {!startDetails.started && (
                      <Text as="div" muted>
                        Starts: {startTime}
                      </Text>
                    )}

                    <Text as="div" muted>
                      Ends: {expirationDetails.expirationDay}
                    </Text>
                  </Box>
                );
              }

              return 'Not Silenced';
            }
          },
          {
            label: 'Ack State',
            name: ALERT_TABLE_COLUMNS.ACK_STATE_LABEL,
            id: ALERT_TABLE_COLUMNS.ACK_STATE_LABEL,
            value: ALERT_TABLE_COLUMNS.ACK_STATE_LABEL,
            computed: true,
            sortable: false,
            flexBasis: 100,
            minWidth: 130,
            renderer: ({ model, value }) => {
              const userModel = $users.collection.modelById[model.ackUserId];
              // userModel should be present but we do not want to throw an error if not
              return userModel ? (
                <Box>
                  <Text as="div">{model.isAutoAcked ? 'Auto-acked' : value}</Text>
                  {model.isAcked && (
                    <>
                      <Text as="div" muted>
                        by {userModel.get('user_full_name')}
                      </Text>
                      <Text as="div" muted>
                        {formatDateTime(model.ackTimestamp, DEFAULT_DATETIME_FORMAT_WITH_SECONDS)}
                      </Text>
                    </>
                  )}
                </Box>
              ) : (
                value || null
              );
            }
          }
        ]
      );
    }

    if (hideColumns) {
      columns = columns.filter((column) => !hideColumns.includes(column.id));
    }

    return columns;
  }

  get kebabActions() {
    const { $app, $alerting, miniKebab } = this.props;
    const { loadingSuppressions, loadingSilences } = this.state;

    const viewDetails = {
      id: 'view-details',
      label: 'View Alert Details',
      icon: 'zoom-in',
      handler: this.handleViewDetails
    };

    if ($app.isExport) {
      return undefined;
    }

    if ($app.isSubtenant || miniKebab) {
      return [viewDetails];
    }

    return [
      viewDetails,
      {
        id: 'divider1',
        renderer: () => <MenuDivider key="divider1" />
      },
      {
        id: 'acknowledge',
        hidden: (model) => !model.canAck || model.isAcked,
        icon: 'confirm',
        label: 'Ack Alert',
        handler: this.handleAckAlertDialog
      },
      {
        id: 'clear',
        hidden: (model) => model.isCleared,
        icon: 'tick',
        label: 'Clear Alert...',
        handler: this.handleClearAlertDialog
      },
      {
        id: 'unacknowledge',
        hidden: (model) => !model.canAck || !model.isAcked,
        renderer: (model) => <UnackAlertButton model={model} asMenuItem key="unacknowledge" />
      },
      {
        id: 'silence',
        hidden: (model) => !model.canSilence || loadingSilences,
        renderer: (model) => <SilenceAlertButton alertModel={model} asMenuItem key="silence" />
      },
      {
        id: 'suppress',
        hidden: (model) => !model.canSuppress || loadingSuppressions,
        renderer: (model) => (
          <SuppressAlertButton
            key="suppress"
            mr={0}
            alertModel={model}
            asMenuItem
            comment="Alert"
            buttonProps={{ icon: null }}
          />
        )
      },
      {
        id: 'comment',
        icon: AiOutlineComment,
        label: 'Add Comment',
        handler: this.onHandleComment
      },
      {
        id: 'divider2',
        renderer: () => <MenuDivider key="divider2" />,
        hidden: (model) => model.isMetricPolicy || model.isNmsPolicy
      },
      {
        id: 'view-dashboard',
        label: 'Open Dashboard',
        hidden: (model) => {
          const policy = model.get('policy');

          return model.isMetricPolicy || (policy && !policy.get('dashboardID'));
        },
        renderer: (model) => {
          const policy = model.get('policy');

          // TODO in theory hidden ensures that policy is always present, however there may be race conditions
          return policy ? (
            <DashboardLink
              as="MenuItem"
              key="dashboard"
              withIcon
              id={policy.get('dashboardID')}
              query={$alerting.getDashboardQuery(model)}
            />
          ) : null;
        }
      },
      {
        id: 'debug',
        icon: AiFillBug,
        label: 'Debug Alert',
        handler: this.handleDebug
      }
    ];
  }

  onHandleComment = (model) => {
    this.setState({ commentModel: model });
  };

  renderMetricColumn = (model) => {
    // NMS Alert
    if (model.isNmsPolicy) {
      return <NmsNativeAlertMetricListRenderer alertModel={model} numMetricsToShow={2} />;
    }

    if (model.isEventPolicy) {
      return metricLabelRenderer(model.policy.metrics[0], null, {
        round: true,
        fontWeight: 'medium',
        p: '2px 6px'
      });
    }

    const metricToValue = model.get('metricToValue') || {};

    return model.sortedMetrics.slice(0, 1).map((metricKey) => {
      const metricStorageEntry = model.policyMeasurement?.storage?.Metrics[metricKey] || {};
      if (model.isStateChangeAlert && !model.isCustomStateChangeAlert) {
        const values = metricStorageEntry?.Values || {};
        const value = values?.[metricToValue[metricKey]];
        return (
          <Fragment key={metricKey}>
            <Flex gap="4px" alignItems="center" flexWrap="wrap">
              <Text as="div" whiteSpace="noWrap" ellipsis muted>
                {metricStorageEntry.Label}:
              </Text>
              {model.isDeviceStateChangeAlert && <DeviceStatusTag value={value} />}
              {model.isInterfaceStateChangeAlert && <InterfaceOperAdminStatusTag value={value} showPrefix />}
              {model.isBGPNeighborStateChangeAlert && <SessionStateRenderer availableStates={values} value={value} />}
            </Flex>
          </Fragment>
        );
      }

      const metricFormat = formatMetricValue(metricToValue[metricKey], metricKey);
      return <Text key={metricKey}>{`${metricFormat.value} ${metricStorageEntry.Label || metricFormat.metric}`}</Text>;
    });
  };

  handleViewDetails = (model) => {
    const path = `/v4/alerting/${model.id}`;
    window.open(path, '_blank');
  };

  handleOnSort = (sortState) => {
    const { $exports } = this.props;

    $exports.setHash({ sortState });
  };

  handleDebug = (model) => {
    this.setState({ debugModel: model });
  };

  handleAckAlertDialog = (model) => {
    this.setState({ ackAlertModel: model });
  };

  handleClearAlertDialog = (model) => {
    this.setState({ clearAlertModel: model });
  };

  handleSubmitClearAlert = (formValues) => {
    const { $comments, $alerting } = this.props;
    const { clearAlertModel } = this.state;
    const { comment } = formValues || {};

    this.handleClearAlertDialog(null);

    $alerting.clearAlarms(clearAlertModel, { comment, toast: true }).then(() => {
      $comments.collection.fetch({ force: true });
    });
  };

  handleSubmitAckAlert = (formValues) => {
    const { $comments, $alerting } = this.props;
    const { ackAlertModel } = this.state;
    const { comment, autoAck, silence } = formValues || {};
    this.handleAckAlertDialog(null);
    $alerting.acknowledgeAlarm(ackAlertModel, { comment, autoAck, silence, toast: true }).then(() => {
      $comments.collection.fetch({ force: true });
    });
  };

  render() {
    const { $alerting, $app, useVirtualizedTable, onRowClick, tableProps, collection } = this.props;
    const { debugModel, ackAlertModel, clearAlertModel, commentModel } = this.state;

    return (
      <>
        <AdminTable
          guardForDuplicates
          noVirtualizedTable={useVirtualizedTable === false || $app.isExport}
          collection={collection || $alerting.collection}
          defaultColumns={this.defaultColumns}
          columns={this.columns}
          fetchCollectionOnMount={false}
          kebabActions={this.kebabActions}
          maxVisibleColumns={11}
          onRowClick={onRowClick}
          onSort={this.handleOnSort}
          rowAlign="center"
          rowHeight={ROW_HEIGHT}
          searchBar={false}
          showColumnSelector={!$app.isExport}
          tabPrefId="alertManagerAlertingTable"
          showRefreshButton={!$app.isExport}
          showAutoRefresh={!$app.isExport}
          {...tableProps}
        />
        {!!debugModel && (
          <AlertDebugDialog isOpen={!!debugModel} alertModel={debugModel} onClose={() => this.handleDebug(null)} />
        )}
        {!!ackAlertModel && (
          <AckDialog
            isOpen={!!ackAlertModel}
            alertModel={ackAlertModel}
            onClose={() => this.handleAckAlertDialog(null)}
            onSubmit={this.handleSubmitAckAlert}
          />
        )}
        {!!clearAlertModel && (
          <ClearAlarmDialog
            isOpen={!!clearAlertModel}
            alertModels={[clearAlertModel]}
            onClose={() => this.handleClearAlertDialog(null)}
            onSubmit={this.handleSubmitClearAlert}
          />
        )}
        <CommentDialog
          entityType="alert"
          isOpen={!!commentModel}
          model={commentModel}
          onClose={() => this.onHandleComment(null)}
          onComment={() => this.onHandleComment(null)}
        />
      </>
    );
  }
}

export default AlertingTable;
